import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NotbankService } from '../../../../../services/notbank.service';
import { CashoutDraft } from '../cashout-currency.types';
import { buildLatamNotbankCashoutDraft } from '../cashout-draft-builders';
import {
  NOTBANK_KIND_OPTIONS,
  NOTBANK_PIX_TYPE_OPTIONS,
  countryForNotbankCurrency,
  createNotbankBankFormGroup,
  isNotbankBankSelected,
  unwrapNotbankList,
  updateNotbankBankValidators,
} from '../notbank-cashout.logic';
import { getFallbackCashoutTransactionType } from '../cashout-transaction-type.helpers';

@Component({
  selector: 'app-latam-notbank-form',
  templateUrl: './latam-notbank-form.component.html',
  styleUrl: '../../add-cashout.component.scss',
})
export class LatamNotbankFormComponent implements OnInit, OnDestroy {
  @Input() ctx!: any;
  @Output() draftReady = new EventEmitter<CashoutDraft>();

  notbankBanks: any[] = [];
  notbankProvinces: any[] = [];
  loadingBanks = false;
  private sub?: Subscription;

  readonly pixTypeOptions = NOTBANK_PIX_TYPE_OPTIONS;

  constructor(
    private notbankService: NotbankService,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    if (!this.ctx.newTransactionForm.get('notbank_bank')) {
      this.ctx.newTransactionForm.addControl(
        'notbank_bank',
        createNotbankBankFormGroup(this.fb),
      );
    }
    const banks = this.ctx.getBanks?.() ?? [];
    const nb = banks.find((b: any) =>
      String(b?.id || b?.code || b?.name || '')
        .toUpperCase()
        .includes('NOTBANK'),
    );
    if (nb && !this.ctx.newTransactionForm.controls.company_id.value) {
      this.ctx.newTransactionForm.patchValue({
        company_id: nb,
        company_name: nb.name || nb.id,
      });
    }
    if (!this.ctx.newTransactionForm.controls.transaction_type.value) {
      this.ctx.setTransactionType(
        getFallbackCashoutTransactionType(this.ctx.currency) ||
          'Transferencia bancaria',
      );
    }
    // LatAm Notbank uses notbank_bank.number instead of top-level account_number.
    this.ctx.newTransactionForm.get('account_number')?.clearValidators();
    this.ctx.newTransactionForm
      .get('account_number')
      ?.updateValueAndValidity({ emitEvent: false });

    this.sub = this.notbankGroup
      .get('kind')
      ?.valueChanges.subscribe(() =>
        updateNotbankBankValidators(this.notbankGroup, this.ctx.currency),
      );
    updateNotbankBankValidators(this.notbankGroup, this.ctx.currency);
    this.loadBanks();
    if (this.country === 'PE') {
      this.loadProvinces();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  get notbankGroup(): FormGroup {
    return this.ctx.newTransactionForm.get('notbank_bank') as FormGroup;
  }

  get country(): string {
    return countryForNotbankCurrency(this.ctx.currency);
  }

  get kindOptions(): { value: string; label: string }[] {
    return NOTBANK_KIND_OPTIONS[this.country] || [];
  }

  get isPix(): boolean {
    return String(this.notbankGroup?.get('kind')?.value || '') === 'br_pix';
  }

  loadBanks(): void {
    this.loadingBanks = true;
    this.notbankService.getBanks({ currency: this.ctx.currency }).subscribe({
      next: (resp: any) => {
        this.loadingBanks = false;
        this.notbankBanks = unwrapNotbankList(resp).map((b: any) => ({
          ...b,
          label: b.name || b.bank_name || b.id,
        }));
      },
      error: () => {
        this.loadingBanks = false;
        this.notbankBanks = [];
        this.ctx.messageService.add({
          severity: 'warn',
          summary: 'Bancos Notbank',
          detail: 'No se pudo cargar el listado de bancos. Reintenta.',
        });
      },
    });
  }

  loadProvinces(): void {
    this.notbankService.getProvinces({ currency: this.ctx.currency }).subscribe({
      next: (resp: any) => {
        this.notbankProvinces = unwrapNotbankList(resp).map((p: any) => ({
          ...p,
          label: p.name || p.province || p.id,
        }));
      },
      error: () => {
        this.notbankProvinces = [];
      },
    });
  }

  onKindChange(): void {
    updateNotbankBankValidators(this.notbankGroup, this.ctx.currency);
  }

  saveTransaction(): void {
    const draft = buildLatamNotbankCashoutDraft(this.ctx);
    if (draft) {
      this.draftReady.emit(draft);
    }
  }

  isNotbankSelected(): boolean {
    return isNotbankBankSelected(this.ctx);
  }
}
